import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLocaleNavigate } from '../../../../hooks/useLocaleNavigate';

import { Box, Typography, Button, Tabs, IconButton, CartIcon, TabItem } from '@lucid/lego';
import { useStore } from '../../../../store/store';
import { useLabels } from '../../../../hooks/useLabels';
import { convertToContentfulLocale, getCurrencyCodeForLocale } from '../../../../utils/tools';
import type { InventoryTableRow } from '../../../../components/Configurator/utils';
import AvailableVehicles from '../../../../components/AvailableVehicles/AvailableVehicles';
import { useGetCheckoutByShortcode } from '../../../../api/services/lupp/inventory/inventory';
import { useCartContext, useCreateCart, CartApi, useAddItemsToCart, CartItem } from '@lucid/cart';
import { ImageBasedConfigurator } from '@rmp-configurator/ui';
import '@rmp-configurator/ui/styles';
import styles from './Content.module.scss';

const CONFIGURATOR_BASE_URL = import.meta.env.VITE_CONFIGURATOR_BASE_URL;

export type ContentProps = {
  theme: 'dark' | 'light';
  locale: string;
};

/**
 * Order Builder page content — rendered inside CartProvider to access cart context.
 * @component
 * @param {ContentProps} props
 * @returns {JSX.Element}
 * @example
 * <Content theme="light" locale="en-US" />
 */
const Content: React.FC<ContentProps> = ({ theme, locale }) => {
  const userIdentity = useStore((state) => state.userIdentity);
  const customerId = userIdentity?.userPortalId ?? '';
  const navigate = useLocaleNavigate();
  const rawNavigate = useNavigate();
  const location = useLocation();
  const activeTab = location.hash.replace('#', '') || 'available-vehicles';

  const { data: labels } = useLabels({
    id: '',
    locale: convertToContentfulLocale(locale),
  });

  const [selectedVehicles, setSelectedVehicles] = useState<InventoryTableRow[]>([]);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isRemovingFromCart, setIsRemovingFromCart] = useState(false);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [cartError, setCartError] = useState<string | null>(null);

  useEffect(() => {
    if (!cartError) return;
    const timeoutId = setTimeout(() => setCartError(null), 3000);
    return () => clearTimeout(timeoutId);
  }, [cartError]);

  const { activeCart, refetchCart } = useCartContext();
  const { mutateAsync: createCart } = useCreateCart({ customerId });
  const { mutateAsync: addCartItems } = useAddItemsToCart();
  const cartItemCount = activeCart?.items?.length ?? 0;
  const { mutateAsync: checkVehicleByShortcode } = useGetCheckoutByShortcode();

  const cartItemVins = useMemo(
    () => activeCart?.items?.map((item) => item.itemCode) ?? [],
    [activeCart?.items]
  );

  const cartVinSet = useMemo(() => new Set(cartItemVins), [cartItemVins]);

  const selectedCartVehicles = useMemo(
    () => selectedVehicles.filter((v) => cartVinSet.has(v.vin)),
    [selectedVehicles, cartVinSet]
  );

  const selectedNonCartVehicles = useMemo(
    () => selectedVehicles.filter((v) => !cartVinSet.has(v.vin)),
    [selectedVehicles, cartVinSet]
  );

  const handleVehicleSelect = useCallback((_vehicle: InventoryTableRow) => {
    // Single-row click — no action needed; selection handles multi-select
  }, []);

  const handleSelectionChange = useCallback((selected: InventoryTableRow[]) => {
    setSelectedVehicles(selected);
  }, []);

  const handleAddToCart = useCallback(async () => {
    if (selectedNonCartVehicles.length === 0) return;
    setIsAddingToCart(true);
    try {
      const cartItems: CartItem[] = selectedNonCartVehicles.map((vehicle) => ({
        itemName: vehicle.model ?? vehicle.vin,
        itemCode: vehicle.vin,
        itemDescription: vehicle.batchNumber,
        itemCategory: 'VEHICLE',
        unitOfMeasurement: 'EA',
        quantityOrdered: 1,
        unitPrice: vehicle.price,
        itemContext: { ...vehicle },
      }));

      let cartId = activeCart?.cartId;

      if (!cartId) {
        const newCart = await createCart({
          customerContext: { customerId },
          currencyCode: getCurrencyCodeForLocale(locale),
        });
        cartId = newCart.cart.cartId;
      }

      await addCartItems({ cartId: cartId!, customerId, items: cartItems });

      setCartError(null);
      setSelectedVehicles([]);
      setSelectionResetKey((k) => k + 1);
      void refetchCart();
    } catch {
      setCartError('Failed to add items to cart. Please try again.');
    } finally {
      setIsAddingToCart(false);
    }
  }, [
    selectedNonCartVehicles,
    activeCart,
    createCart,
    addCartItems,
    customerId,
    locale,
    refetchCart,
  ]);

  const handleAddToCartFromConfigurator = useCallback(
    async (shortCode: string) => {
      try {
        const vehicle = await checkVehicleByShortcode({
          shortCode: shortCode,
          orderChannel: 'BUILD_ORDER',
        });

        const cartItems: CartItem[] = [
          {
            itemName: vehicle.model ?? vehicle.vin,
            itemCode: vehicle.shortcode,
            itemDescription: vehicle.vinBatchNumber ?? vehicle.shortcode,
            itemCategory: 'VEHICLE',
            unitOfMeasurement: 'EA',
            quantityOrdered: 1,
            unitPrice: vehicle.pricing.total,
            itemContext: {
              ...vehicle,
              vin: vehicle.vin ?? vehicle.shortcode,
              price: vehicle.pricing.total,
            },
          },
        ];

        let cartId = activeCart?.cartId;
        if (!cartId) {
          const newCart = await createCart({
            customerContext: { customerId },
            currencyCode: getCurrencyCodeForLocale(locale),
          });
          cartId = newCart.cart.cartId;
        }

        await addCartItems({ cartId: cartId!, customerId, items: cartItems });

        setSelectedVehicles([]);
        setSelectionResetKey((k) => k + 1);
        void refetchCart();
      } catch {
        setCartError('Failed to add items to cart. Please try again.');
      } finally {
        setIsAddingToCart(false);
      }
    },
    [
      addCartItems,
      checkVehicleByShortcode,
      setIsAddingToCart,
      createCart,
      activeCart?.cartId,
      locale,
      customerId,
      refetchCart,
    ]
  );

  const handleRemoveFromCart = useCallback(async () => {
    if (selectedCartVehicles.length === 0) return;
    const cartId = activeCart?.cartId;
    if (!cartId) return;
    setIsRemovingFromCart(true);
    try {
      await Promise.all(
        selectedCartVehicles.map((vehicle) => {
          const item = activeCart?.items?.find((i) => i.itemCode === vehicle.vin);
          if (!item?.itemId) return Promise.resolve();
          return CartApi.delete(`/v1/carts/${cartId}/items/${item.itemId}`, {
            headers: { 'X-Customer-Id': customerId },
          });
        })
      );
      setCartError(null);
      setSelectedVehicles([]);
      setSelectionResetKey((k) => k + 1);
      void refetchCart();
    } catch {
      setCartError('Failed to remove items from cart. Please try again.');
    } finally {
      setIsRemovingFromCart(false);
    }
  }, [selectedCartVehicles, activeCart, customerId, refetchCart]);

  const cartToolbarButton = useMemo(
    () => (
      <div className={styles.toolbarActions}>
        {cartItemVins.length > 0 && (
          <Button
            theme={theme}
            size="small"
            type="primary"
            color="error-2"
            dataTestId="remove-from-cart-button"
            gaComponent="order-builder"
            onClick={handleRemoveFromCart}
            disabled={selectedCartVehicles.length === 0 || isRemovingFromCart}
            iconLeft={{ category: 'global', name: 'trash' }}
            label={
              isRemovingFromCart
                ? (labels?.removing ?? 'Removing...')
                : (labels?.removeFromCart ?? 'Remove from Cart')
            }
          />
        )}
        <Button
          theme={theme}
          size="small"
          type="primary"
          dataTestId="add-to-cart-button"
          gaComponent="order-builder"
          onClick={handleAddToCart}
          disabled={selectedNonCartVehicles.length === 0 || isAddingToCart}
          iconLeft={{ category: 'global', name: 'cart' }}
          label={
            isAddingToCart ? (labels?.adding ?? 'Adding...') : (labels?.addToCart ?? 'Add to Cart')
          }
        />
      </div>
    ),
    [
      theme,
      handleAddToCart,
      handleRemoveFromCart,
      selectedNonCartVehicles.length,
      selectedCartVehicles.length,
      cartItemVins.length,
      isAddingToCart,
      isRemovingFromCart,
      labels,
    ]
  );

  const tabs: TabItem[] = useMemo(
    () => [
      {
        id: 'available-vehicles',
        label: labels?.availableVehicles ?? 'Available Vehicles',
        content: (
          <>
            {cartError && (
              <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.cartError}>
                <Typography variant="body" size="medium" color="white" theme={theme}>
                  {cartError}
                </Typography>
              </Box>
            )}
            <AvailableVehicles
              onVehicleSelect={handleVehicleSelect}
              onSelectionChange={handleSelectionChange}
              toolbarContent={cartToolbarButton}
              cartVins={cartItemVins}
              selectionResetKey={selectionResetKey}
              dataTestId="order-builder-inventory"
            />
          </>
        ),
      },
      {
        id: 'configurator',
        label: labels?.configurator ?? 'Configurator',
        content: (
          <ImageBasedConfigurator
            onlyShowcart={true}
            configureTab={{ showCart: false, defaultRegion: 'de-de' }}
            showTabs={{
              configure: true,
            }}
            apiUrl={CONFIGURATOR_BASE_URL}
            hideFilterBar={true}
            addToGlobalCart={handleAddToCartFromConfigurator}
          />
        ),
      },
    ],
    [
      labels,
      handleVehicleSelect,
      handleSelectionChange,
      handleAddToCartFromConfigurator,
      cartToolbarButton,
      cartItemVins,
      selectionResetKey,
      cartError,
      theme,
    ]
  );

  return (
    <Box theme={theme} data-testid="order-builder-page">
      <Box className={styles.header}>
        <Box>
          <Typography variant="headline" size="small" color="primary-1" theme={theme}>
            {labels?.orderBuilder ?? 'Order Builder'}
          </Typography>
          <Typography variant="body" size="medium" color="gray-3" theme={theme}>
            {labels?.orderBuilderDescription ??
              'Configure the vehicle you wish to add to your bulk order.'}
          </Typography>
        </Box>
        <IconButton
          theme={theme}
          size="large"
          color="brand"
          buttonType="primary"
          dataTestId="cart-button"
          gaComponent="order-builder"
          handleClick={() => navigate('/order-hub/order-builder/checkout')}
          customIcon={
            <CartIcon size={32} color="white" count={cartItemCount} badgeColor="#000000" />
          }
          shape="circle"
        />
      </Box>
      <Box pt={24}>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          defaultActiveTab="available-vehicles"
          onTabChange={(tabId) => rawNavigate({ hash: tabId }, { replace: true })}
          theme={theme}
          dataTestId="order-builder-tabs"
        />
      </Box>
    </Box>
  );
};

export default Content;
